import { useState, useEffect } from "react";
import { getRewards, createReward, updateReward, deleteReward } from "../services/rewards.js";

const REWARD_CATS = ["Airtime","Data","Voucher","Subscription","Gift Card","Cash"];
const CAT_EMOJI = {Airtime:"📱",Data:"🌐",Voucher:"🛍️",Subscription:"🎬",GiftCard:"📦",Cash:"💸"};

export function RewardAdmin({onBack}) {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({name:"",cat:"Airtime",cost:"",desc:"",icon:""});
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch all rewards from API (includes both seed + admin-created, active only)
  const fetchRewards = () => {
    setLoading(true);
    getRewards()
      .then(data => {
        if (data?.length) setAllItems(data.map(r => ({
          id: r.id,
          name: r.name,
          icon: r.icon ?? "🎁",
          cost: r.xp_cost ?? r.xpCost ?? 0,
          cat: r.category ?? "Airtime",
          provider: r.provider ?? "",
          desc: r.description ?? "",
          source: r.source ?? "seed",
        })));
        else setAllItems([]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRewards(); }, []);

  const save = async () => {
    if(!form.name||!form.cost) return;
    setSaving(true);
    try {
      if (editId) {
        await updateReward(editId, {
          name: form.name,
          xpCost: parseInt(form.cost)||0,
          description: form.desc,
        });
      } else {
        await createReward({
          name: form.name,
          category: form.cat,
          xpCost: parseInt(form.cost)||0,
          description: form.desc,
          icon: form.icon||(CAT_EMOJI[form.cat.replace(/\s/g,"")]||"🎁"),
          provider: "Custom",
        });
      }
      fetchRewards();
      setShowForm(false); setEditId(null);
      setForm({name:"",cat:"Airtime",cost:"",desc:"",icon:""});
    } catch { /* show error? */ }
    finally { setSaving(false); }
  };

  const startEdit = (r) => {
    setForm({name:r.name,cat:r.cat,cost:String(r.cost),desc:r.desc||"",icon:r.icon||""});
    setEditId(r.id); setShowForm(true);
  };

  const remove = async (id) => {
    try { await deleteReward(id); fetchRewards(); }
    catch { /* ignore */ }
    setConfirmRemove(null);
  };

  const customCount = allItems.filter(r=>r.source==="admin").length;
  const F = {width:"100%",padding:"10px 12px",background:"#090e1a",border:"1px solid #1e2d45",borderRadius:9,color:"#e2e8f0",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"};

  return (
    <div style={{maxWidth:860,margin:"0 auto",padding:"32px 18px 60px"}}>
      <button onClick={onBack} style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:13,marginBottom:22,padding:0}}>← Back</button>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:28,gap:12}}>
        <div>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.15em",color:"#f59e0b",textTransform:"uppercase",marginBottom:6}}>Admin Console</div>
          <h1 style={{fontSize:26,fontWeight:900,color:"#f1f5f9",margin:0,fontFamily:"Georgia,serif"}}>Reward Shop</h1>
          <p style={{color:"#64748b",marginTop:4,fontSize:13}}>{loading ? "Loading…" : `${allItems.length} reward items · ${customCount} custom`}</p>
        </div>
        <button onClick={()=>{setEditId(null);setForm({name:"",cat:"Airtime",cost:"",desc:"",icon:""});setShowForm(true);}}
          style={{padding:"10px 18px",background:"linear-gradient(135deg,#f59e0b,#d97706)",color:"#000",border:"none",borderRadius:9,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
          + Add Reward
        </button>
      </div>

      {/* Rewards table */}
      <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:13,overflow:"hidden",marginBottom:20}}>
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 80px 2fr 120px",gap:0,borderBottom:"1px solid #1e2d45",padding:"10px 16px"}}>
          {["Name","Category","XP Cost","Description","Actions"].map(h=>(
            <div key={h} style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.08em"}}>{h}</div>
          ))}
        </div>
        {allItems.map((item,i)=>{
          const isCustom = item.source === "admin";
          return (
            <div key={item.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 80px 2fr 120px",gap:0,padding:"12px 16px",borderBottom:i<allItems.length-1?"1px solid #0f1a2a":"none",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:16}}>{item.icon}</span>
                <div>
                  <div style={{color:"#f1f5f9",fontSize:13,fontWeight:600}}>{item.name}</div>
                  <div style={{color:"#475569",fontSize:11}}>{item.provider}</div>
                </div>
              </div>
              <div style={{color:"#64748b",fontSize:12}}>{item.cat}</div>
              <div style={{color:"#f59e0b",fontWeight:700,fontSize:13}}> {item.cost}</div>
              <div style={{color:"#475569",fontSize:11,lineHeight:1.4}}>{item.desc||"—"}</div>
              <div style={{display:"flex",gap:5}}>
                {isCustom ? (
                  <>
                    <button onClick={()=>startEdit(item)}
                      style={{padding:"5px 10px",background:"#1e2d45",border:"none",borderRadius:7,color:"#94a3b8",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                    <button onClick={()=>setConfirmRemove(item)}
                      style={{padding:"5px 10px",background:"#ef444418",border:"1px solid #ef444430",borderRadius:7,color:"#ef4444",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>
                  </>
                ) : (
                  <span style={{fontSize:10,color:"#334155",padding:"5px 8px"}}>Built-in</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit form modal */}
      {showForm && (
        <div style={{position:"fixed",inset:0,background:"#000000bb",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:18,padding:"28px 24px",maxWidth:420,width:"100%",animation:"popIn .3s ease"}}>
            <h3 style={{color:"#f1f5f9",fontWeight:900,fontFamily:"Georgia,serif",fontSize:18,margin:"0 0 20px"}}>{editId?"Edit Reward":"Add New Reward"}</h3>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Name *</div>
                <input style={F} placeholder="e.g. ₦200 Airtime" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Category</div>
                  <select style={{...F,appearance:"none"}} value={form.cat} onChange={e=>setForm(f=>({...f,cat:e.target.value}))}>
                    {REWARD_CATS.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>XP Cost *</div>
                  <input style={F} type="number" min="1" placeholder="e.g. 25" value={form.cost} onChange={e=>setForm(f=>({...f,cost:e.target.value}))}/>
                </div>
              </div>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Description</div>
                <input style={F} placeholder="Optional description" value={form.desc} onChange={e=>setForm(f=>({...f,desc:e.target.value}))}/>
              </div>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Emoji Icon</div>
                <input style={F} placeholder="Single emoji (e.g. 📱)" value={form.icon} onChange={e=>setForm(f=>({...f,icon:e.target.value}))} maxLength={2}/>
              </div>
            </div>
            <div style={{display:"flex",gap:9,marginTop:20}}>
              <button onClick={()=>{setShowForm(false);setEditId(null);}}
                style={{flex:1,padding:"11px",background:"transparent",border:"1px solid #1e2d45",borderRadius:9,color:"#64748b",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              <button onClick={save} disabled={!form.name||!form.cost}
                style={{flex:2,padding:"11px",background:form.name&&form.cost?"linear-gradient(135deg,#f59e0b,#d97706)":"#1e2d45",color:form.name&&form.cost?"#000":"#475569",border:"none",borderRadius:9,fontSize:14,fontWeight:700,cursor:form.name&&form.cost?"pointer":"not-allowed",fontFamily:"inherit"}}>
                {editId?"Save Changes":"Add Reward"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm remove modal */}
      {confirmRemove && (
        <div style={{position:"fixed",inset:0,background:"#000000bb",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#0d1724",border:"1px solid #ef444430",borderRadius:18,padding:"24px",maxWidth:340,width:"100%",textAlign:"center",animation:"popIn .3s ease"}}>
            <div style={{fontSize:32,marginBottom:10}}>⚠️</div>
            <h3 style={{color:"#f1f5f9",fontWeight:900,fontFamily:"Georgia,serif",fontSize:17,margin:"0 0 8px"}}>Remove {confirmRemove.name}?</h3>
            <p style={{color:"#64748b",fontSize:13,margin:"0 0 20px"}}>This will immediately remove it from the Reward Shop.</p>
            <div style={{display:"flex",gap:9}}>
              <button onClick={()=>setConfirmRemove(null)} style={{flex:1,padding:"11px",background:"transparent",border:"1px solid #1e2d45",borderRadius:9,color:"#64748b",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              <button onClick={()=>remove(confirmRemove.id)} style={{flex:2,padding:"11px",background:"linear-gradient(135deg,#ef4444,#dc2626)",color:"#fff",border:"none",borderRadius:9,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RewardAdmin;
